import type { RCard } from 'v2/types/api/billing'

/**
 * Which delete a card needs.
 *
 * `unidentified` is a refusal, not a fallback: a Razorpay card with no token
 * cannot be deleted by the generic route either, and pretending otherwise
 * misroutes it.
 */
export type CardDeleteRoute =
  | { kind: 'razorpay'; tokenId: string }
  | { kind: 'generic'; cardMappingId: number }
  | { kind: 'unidentified' }

/**
 * Deleting a saved card is not one path.
 *
 * A Razorpay card is a TOKEN held at Razorpay and has to go through the token
 * route (`/accounts/{id}/razorpay/cards/{tokenId}`); the generic delete misroutes
 * it to Stripe, so the console reports success while the card stays chargeable.
 * Both PSPs' cards arrive in the SAME `/account/card` list — `useBillingCardsQuery`
 * applies no `stripe_client` filter — so the decision is per-card, never
 * per-account.
 *
 * Kept as a pure function, and consumed by every caller through
 * `useCardDeleteRouting`, because the previous arrangement made each call site
 * responsible for remembering: the Payment methods page did, and the card picker
 * inside the recharge modals did not — which is the surface India accounts
 * actually use.
 *
 * Matching is case-insensitive: `stripe_client` is typed as a bare `string`, so
 * nothing enforces its casing, and the two failure directions are not symmetric —
 * over-matching costs nothing realistic, under-matching sends a live payment token
 * to the wrong gateway.
 */
export const resolveCardDeleteRoute = (card: RCard): CardDeleteRoute => {
  if (card.stripe_client?.toUpperCase() === 'RAZORPAY') {
    // The token is the card's payment_intent_id (= backend stripe_payment_id) from
    // the shared card list — no separate lookup or last-4 matching needed.
    return card.payment_intent_id
      ? { kind: 'razorpay', tokenId: card.payment_intent_id }
      : { kind: 'unidentified' }
  }
  return { kind: 'generic', cardMappingId: card.card_mapping_id }
}
